/**
 * Customer-facing ETA-card copy. **Single source of truth** for the strings rendered on the
 * customer-web and customer-mobile order-tracking surfaces.
 *
 * **Why this module exists:** the running-late state's wording carries compliance weight (the
 * customer-disclosure rule from `memory/feedback_customer_driver_disclosure.md`: customers can
 * be told the driver is making other stops, but never *how many*, *when*, or *where*). The
 * customer-web `eta-card-body.test.tsx` enforces that property with regex assertions on the
 * rendered DOM. With the strings duplicated across web + mobile, a future copy edit on the
 * mobile surface could drift past the web's regex gate. Pinning both surfaces to the same
 * constants here means a copy change on either app changes both at once, and the existing
 * customer-web disclosure-rule tests catch any regression on either surface.
 *
 * **Adding a string:** add it here, render it from both surfaces. Don't inline a literal in
 * either app's component — it'll skip the regex gate. Tests live in
 * `apps/customer-web/src/dispensary/eta-card-body.test.tsx`.
 */
export const ETA_COPY = {
    preRun: {
        heading: "Preparing your order",
        body: "We'll share an estimate once your driver is on the road.",
    },
    window: {
        eyebrow: "Arriving",
        body: "Your driver is on the way.",
    },
    runningLate: {
        heading: "Running a little late",
        /**
         * Generic-only — "a few other stops along the way" deliberately avoids any count, address,
         * or relative timing. The customer-web disclosure-rule test asserts the rendered DOM has
         * no `\d+ stops?`, no `dispensary`, and no `before/after his/her/their`. If you edit this
         * string, run `pnpm --filter @the-drop/customer-web test eta-card-body` to verify the
         * gate still holds.
         */
        body: "Your driver is making a few other stops along the way.",
        /** Prefix for the new window in the running-late state. The `– ` separator is appended by
         *  the renderer between formatted start + end labels. */
        estimatePrefix: "New estimate:",
    },
};
//# sourceMappingURL=eta-copy.js.map